import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { components, Lettability, LettabilityConfirmation, LettabilityLots, PackReleaseView } from '../../api/schema'
import { useCan } from '../../contexts/PermissionsContext'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { useFormatters } from '../../lib/format'
import { lotBaseTypeCode } from '../../lib/asset-type-tree'
import { AssetProperties } from '../AssetProperties'
import { useAssetProperties, useAssetTypes, usePropertyDefinitionsByCodes } from '../../hooks/useQueries'
import { useLeaseRegimes } from '../LeaseVerdictBadge'
import { AssetListingCard } from '../listings/AssetListingCard'
import { ScreeningPolicyCard } from '../rentalApplications/ScreeningPolicyCard'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'

type Status = 'allowed' | 'blocked' | 'not_evaluated'

const STATUS_VARIANT: Record<Status, 'success' | 'destructive' | 'warning'> = {
    allowed: 'success',
    blocked: 'destructive',
    not_evaluated: 'warning',
}

const CRITERION_VARIANT: Record<string, 'success' | 'destructive' | 'warning'> = {
    COMPLIANT: 'success',
    NON_COMPLIANT: 'destructive',
    UNKNOWN: 'warning',
    ERROR: 'warning',
}

/** The gate's state, as a badge. Carries `data-status` for tests and styling. */
export function LettabilityStatusBadge({ status, testId }: { status: string; testId?: string }) {
    const { t } = useTranslation('leases')
    return (
        <Badge variant={STATUS_VARIANT[status as Status] ?? 'warning'} data-testid={testId} data-status={status}>
            {t(`occupation.status.${status}`, { defaultValue: status })}
        </Badge>
    )
}

/**
 * The Occupation tab (spec 2026-10-25-04 step 12): on a lot, the running lease,
 * the letting regime, the lettability gate with its criteria and the
 * confirmations; on a building or site, the gate over its lots. No criterion is
 * named here: labels come from the rules.
 */
export function OccupationTab({ org, assetUid, assetTypeCode, isRoomInLot }: { org: string; assetUid: string; assetTypeCode?: string; isRoomInLot?: boolean }) {
    const isLot = lotBaseTypeCode(assetTypeCode) === 'unit'
    return (
        <div className="space-y-4">
            {isRoomInLot
                ? <RoomOccupation org={org} assetUid={assetUid} />
                : isLot ? <LotOccupation org={org} assetUid={assetUid} /> : <SubtreeOccupation org={org} assetUid={assetUid} />}
            {/* The selection policy of the building, lot or room, inherited downwards (spec 2026-10-25-11). */}
            <ScreeningPolicyCard org={org} assetUid={assetUid} />
        </div>
    )
}

type AvailabilityRow = components['schemas']['Availability']

const AVAILABILITY_VARIANT: Record<string, 'success' | 'destructive' | 'warning' | 'secondary' | 'outline'> = {
    occupied: 'secondary',
    let: 'outline',
    not_lettable: 'destructive',
    let_agreed: 'outline',
    under_offer: 'warning',
    available_from: 'success',
}

/** Refetch the gate, the availability and the letting properties of the page. */
function useRefreshOccupation() {
    const queryClient = useQueryClient()
    return () => queryClient.invalidateQueries({
        predicate: (q) => {
            const [method, path] = q.queryKey as [string, string]
            return method === 'get' && typeof path === 'string' && (
                path === '/api/v1/organizations/{orgCode}/availability' ||
                path.startsWith('/api/v1/organizations/{orgCode}/assets/{assetUid}')
            )
        },
    })
}

async function putLetting(org: string, assetUid: string, body: { lettable?: boolean | null; relet_ready_on?: string | null }) {
    await apiClient.PUT('/api/v1/organizations/{orgCode}/assets/{assetUid}/letting', {
        params: { path: { orgCode: org, assetUid } },
        body,
    })
}

/**
 * When the object can be let (spec 2026-10-25-06 step 8): its commercial
 * status, the date and why, the lease it waits on and the next one. Read off
 * the leases, so it is offered with contracts.read only. The ready-to-re-let
 * date is the one input: everything else is computed.
 */
function AvailabilitySection({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate } = useFormatters()
    const canWrite = useCan('assets.write')
    const refresh = useRefreshOccupation()
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/availability', {
        params: { path: { orgCode: org }, query: { root_asset_uid: assetUid } },
    })
    const { data: ready } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
        params: { path: { orgCode: org, assetUid, propertyUid: 'relet_ready_on' } },
    }, { retry: false })
    const readyValue = typeof (ready as { value?: unknown } | undefined)?.value === 'string' ? ((ready as { value: string }).value).slice(0, 10) : ''
    const [readyOn, setReadyOn] = useState(readyValue)
    useEffect(() => setReadyOn(readyValue), [readyValue])
    const row = (data?.data as AvailabilityRow[] | undefined)?.find((r) => r.asset_uid === assetUid)

    const saveReady = async (value: string) => {
        setReadyOn(value)
        await putLetting(org, assetUid, { relet_ready_on: value || null })
        await refresh()
    }

    return (
        <Card data-testid="availability-section">
            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
                <CardTitle className="text-base">{t('availability.title')}</CardTitle>
                {row && (
                    <Badge variant={AVAILABILITY_VARIANT[row.commercial_status] ?? 'outline'} data-testid="availability-status" data-status={row.commercial_status}>
                        {t(`availability.status.${row.commercial_status}`)}
                    </Badge>
                )}
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {!row ? <Skeleton className="h-10 w-full" /> : (
                    <>
                        {row.commercial_status === 'available_from' && (
                            <div data-testid="availability-date" data-date={row.available_from ?? ''}>
                                {row.available_from ? t('availability.date', { date: formatDate(row.available_from) }) : t('availability.date_unknown')}
                                {row.available_from_reason && (
                                    <span className="text-xs text-muted-foreground"> · {t(`availability.reason.${row.available_from_reason}`)}</span>
                                )}
                            </div>
                        )}
                        {row.current_lease_uid && (
                            <div className="flex flex-wrap items-center gap-x-2">
                                <span className="text-xs text-muted-foreground">{t('availability.current_lease')}:</span>
                                <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: row.current_lease_uid }}
                                    className="text-primary hover:underline" data-testid="availability-lease-link">
                                    {row.current_lease_ends_on ? t('availability.ends_on', { date: formatDate(row.current_lease_ends_on) }) : t('open_ended')}
                                </Link>
                                {row.notice_received_on && (
                                    <span className="text-xs text-muted-foreground" data-testid="availability-notice">
                                        {t('availability.notice', { date: formatDate(row.notice_received_on), by: t(`availability.by.${row.notice_given_by ?? 'tenant'}`) })}
                                    </span>
                                )}
                            </div>
                        )}
                        {row.next_lease_uid && (
                            <div className="flex flex-wrap items-center gap-x-2">
                                <span className="text-xs text-muted-foreground">{t('availability.next_lease')}:</span>
                                <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: row.next_lease_uid }}
                                    className="text-primary hover:underline" data-testid="availability-next-lease-link">
                                    {row.next_lease_starts_on ? t('availability.starts_on', { date: formatDate(row.next_lease_starts_on) }) : '—'}
                                </Link>
                            </div>
                        )}
                    </>
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Label htmlFor={`availability-ready-on-${assetUid}`} className="text-xs text-muted-foreground">{t('availability.ready_on')}</Label>
                    <Input id={`availability-ready-on-${assetUid}`} type="date" className="w-44" value={readyOn} disabled={!canWrite}
                        onChange={(e) => void saveReady(e.target.value)} data-testid="availability-ready-on" />
                </div>
                <p className="text-xs text-muted-foreground">{t('availability.ready_on_help')}</p>
            </CardContent>
        </Card>
    )
}

/** A room inside a lot: let on its own only when declared so; then its gate composes its lot's. */
function RoomOccupation({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const canReadContracts = useCan('contracts.read')
    const canWrite = useCan('assets.write')
    const refresh = useRefreshOccupation()
    const [saving, setSaving] = useState(false)
    const { data: declared } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
        params: { path: { orgCode: org, assetUid, propertyUid: 'lettable' } },
    }, { retry: false })
    const lettable = (declared as { value?: unknown } | undefined)?.value === true
    const { data: gate } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/lettability', {
        params: { path: { orgCode: org, assetUid } },
    }, { enabled: lettable })

    const toggle = async (checked: boolean) => {
        setSaving(true)
        try {
            await putLetting(org, assetUid, { lettable: checked })
            await refresh()
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="space-y-4" data-testid="occupation-tab">
            <Card data-testid="room-lettable-card">
                <CardContent className="space-y-1 p-4 text-sm">
                    <label className="flex items-center gap-2">
                        <Checkbox checked={lettable} disabled={!canWrite || saving}
                            onCheckedChange={(v) => void toggle(v === true)} data-testid="room-lettable-checkbox" />
                        <span>{t('availability.room_lettable')}</span>
                    </label>
                    <p className="text-xs text-muted-foreground">{t('availability.room_lettable_help')}</p>
                </CardContent>
            </Card>
            {lettable && (gate ? <LettabilityGateCard org={org} assetUid={assetUid} gate={gate as Lettability} /> : <Skeleton className="h-40 w-full" />)}
            {lettable && canReadContracts && <AvailabilitySection org={org} assetUid={assetUid} />}
        </div>
    )
}

function useRentalCompliance(org: string, assetUid: string, enabled: boolean) {
    return $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/rental-compliance', {
        params: { path: { orgCode: org, assetUid } },
    }, { enabled })
}

function LotOccupation({ org, assetUid }: { org: string; assetUid: string }) {
    const canReadContracts = useCan('contracts.read')
    const { data: gate, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/lettability', {
        params: { path: { orgCode: org, assetUid } },
    }, {
        // A save derives the scope within a short budget and leaves the rest to
        // the async pass: a regime already set whose scope is still unknown is
        // that pass in flight, so the gate is read again until it lands.
        refetchInterval: (q) => {
            const g = q.state.data as Lettability | undefined
            return g?.letting_regime && g.reasons?.some((r) => r.code === 'letting_regime_unknown') ? 2000 : false
        },
    })
    return (
        <div className="space-y-4" data-testid="occupation-tab">
            {canReadContracts && <LeaseCard org={org} assetUid={assetUid} />}
            {canReadContracts && <AvailabilitySection org={org} assetUid={assetUid} />}
            <AssetListingCard org={org} assetUid={assetUid} />
            <RegimeCard org={org} assetUid={assetUid} gate={gate as Lettability | undefined} />
            {isLoading || !gate ? <Skeleton className="h-40 w-full" /> : (
                <>
                    <LettabilityGateCard org={org} assetUid={assetUid} gate={gate as Lettability} />
                    <CriteriaCard org={org} assetUid={assetUid} gate={gate as Lettability} />
                </>
            )}
            <ConfirmationsCard org={org} assetUid={assetUid} />
        </div>
    )
}

function LeaseCard({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const { formatMoney, formatDate } = useFormatters()
    const { data } = useRentalCompliance(org, assetUid, true)
    const lease = data?.leases?.[0]
    return (
        <Card data-testid="occupation-lease-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('occupation.lease.title')}</CardTitle></CardHeader>
            <CardContent className="text-sm">
                {!lease ? (
                    <span className="text-muted-foreground" data-testid="occupation-lease-vacant">{t('occupation.lease.vacant')}</span>
                ) : (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                        <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: lease.contract_uid ?? '' }}
                            className="font-medium text-primary hover:underline" data-testid="occupation-lease-link">
                            {lease.contract_name || t('occupation.lease.unnamed')}
                        </Link>
                        <span data-testid="occupation-lease-rent">
                            {t('occupation.lease.annual_rent')} {formatMoney(lease.annual_rent, data?.currency)}
                        </span>
                        <span data-testid="occupation-lease-term">
                            {t('occupation.lease.term')} {lease.lease_end_date ? formatDate(lease.lease_end_date) : t('open_ended')}
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

function RegimeCard({ org, assetUid, gate }: { org: string; assetUid: string; gate?: Lettability }) {
    const { t } = useTranslation('leases')
    const queryClient = useQueryClient()
    const canWrite = useCan('assets.write')
    const { regimes, regimeLabel } = useLeaseRegimes(org)
    const [saving, setSaving] = useState(false)
    const { data: value } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
        params: { path: { orgCode: org, assetUid, propertyUid: 'letting_regime' } },
    }, { retry: false })
    const source = (value as { source_details?: { source?: string; materialized_by?: string } } | undefined)?.source_details
    const sourceKey = source?.materialized_by === 'occupancies' ? 'lease' : source ? 'manual' : undefined

    const choose = async (code: string) => {
        setSaving(true)
        try {
            await apiClient.PUT('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', {
                params: { path: { orgCode: org, assetUid, propertyUid: 'letting_regime' }, query: { sync: true } },
                body: { value: code },
            })
            await queryClient.invalidateQueries({
                predicate: (q) => {
                    const key = q.queryKey as unknown[]
                    return key[0] === 'get' && typeof key[1] === 'string' && key[1].startsWith('/api/v1/organizations/{orgCode}/assets/{assetUid}')
                },
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <Card data-testid="occupation-regime-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('occupation.regime.title')}</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap items-center gap-3 text-sm">
                <span data-testid="occupation-regime-value" data-regime={gate?.letting_regime ?? ''}>
                    {gate?.letting_regime ? regimeLabel(gate.letting_regime) : t('occupation.regime.unknown')}
                </span>
                {sourceKey && (
                    <Badge variant="outline" data-testid="occupation-regime-source">{t(`occupation.regime.source.${sourceKey}`)}</Badge>
                )}
                {canWrite && (
                    <Select value={gate?.letting_regime ?? ''} onValueChange={(v) => void choose(v)} disabled={saving}>
                        <SelectTrigger className="w-64" data-testid="occupation-regime-select">
                            <SelectValue placeholder={t('occupation.regime.choose')} />
                        </SelectTrigger>
                        <SelectContent>
                            {regimes.map((r) => (
                                <SelectItem key={r.code} value={r.code} data-testid={`occupation-regime-option-${r.code}`}>{r.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <p className="w-full text-xs text-muted-foreground">{t('occupation.regime.help')}</p>
            </CardContent>
        </Card>
    )
}

function label(raw: unknown, locale: string): string {
    return raw && typeof raw === 'object' ? getLocalizedName(raw as Record<string, string>, locale) : ''
}

function PackFooter({ releases }: { releases: PackReleaseView[] }) {
    const { t } = useTranslation('leases')
    if (releases.length === 0) return null
    return (
        <div className="border-t border-border pt-2 text-xs text-muted-foreground" data-testid="occupation-pack-releases">
            {t('occupation.gate.judged_by')}{' '}
            {releases.map((r, i) => (
                <span key={r.uid} data-testid={`occupation-pack-release-${r.code}-${r.jurisdiction}`}>
                    {i > 0 && ', '}
                    {t(`occupation.pack.${r.code}`, { defaultValue: r.code })} {r.jurisdiction} {r.version}
                    {!r.validated && <> ({t('occupation.pack.indicative')})</>}
                </span>
            ))}
        </div>
    )
}

/** The gate card: state, reasons, jurisdiction, blockers and the releases that judged (patterns §52). */
export function LettabilityGateCard({ org, assetUid, gate }: { org: string; assetUid: string; gate: Lettability }) {
    const { t } = useTranslation('leases')
    const { locale } = useLocale()
    const jur = gate.jurisdiction
    return (
        <Card data-testid="occupation-gate-card">
            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
                <CardTitle className="text-base">{t('occupation.gate.title')}</CardTitle>
                <LettabilityStatusBadge status={gate.status} testId="occupation-gate-status" />
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">{t(`occupation.gate.explain.${gate.status}`)}</p>
                <div data-testid="occupation-jurisdiction" data-jurisdiction={jur.code}>
                    {jur.code ? (
                        <span>{t('occupation.gate.jurisdiction', { code: jur.code })} · {t(`occupation.gate.jurisdiction_source.${jur.source}`)}</span>
                    ) : (
                        <span className="text-warning">
                            {t('occupation.gate.jurisdiction_unknown')}{' '}
                            <Link to="/$org/assets/$assetUid" params={{ org, assetUid }} search={{ tab: 'data' }} className="underline">{t('occupation.gate.set_address')}</Link>
                            {' · '}
                            <Link to="/$org/settings/organization" params={{ org }} className="underline">{t('occupation.gate.set_default')}</Link>
                        </span>
                    )}
                </div>
                {gate.reasons.length > 0 && (
                    <ul className="list-disc space-y-1 pl-5" data-testid="occupation-gate-reasons">
                        {gate.reasons.map((r) => (
                            <li key={r.code} data-testid={`occupation-gate-reason-${r.code}`}>{t(`occupation.reason.${r.code}`)}</li>
                        ))}
                    </ul>
                )}
                {gate.blockers.length > 0 && (
                    <ul className="space-y-1" data-testid="occupation-blockers">
                        {gate.blockers.map((b) => (
                            <li key={`${b.kind}-${b.code}-${b.asset_uid ?? ''}`} className="flex flex-wrap items-center gap-2" data-testid={`occupation-blocker-${b.kind}-${b.code}`} data-asset-uid={b.asset_uid ?? ''}>
                                <Badge variant="destructive">{t(`occupation.blocker.${b.kind}`)}</Badge>
                                <span>{label(b.label, locale) || b.code}</span>
                                <span className="text-xs text-muted-foreground">{t(`occupation.blocker_status.${b.status}`, { defaultValue: b.status })}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <PackFooter releases={gate.pack_releases} />
            </CardContent>
        </Card>
    )
}

/** What the verdict stands on: each input property's value and where it came from. */
function CriterionEvidence({ org, assetUid, ruleCode, codes }: { org: string; assetUid: string; ruleCode: string; codes: string[] }) {
    const { t } = useTranslation('leases')
    const { locale } = useLocale()
    const { data: defs } = usePropertyDefinitionsByCodes(org, codes)
    const { data: values } = useAssetProperties(org, assetUid)
    // The lot's own rooms and ventilation / heating equipment, which the in_tree
    // child list leaves out: unfiltered, then narrowed by type code.
    const { data: types } = useAssetTypes(org)
    const { data: kids } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets', {
        params: { path: { orgCode: org }, query: { parent_asset_uid: assetUid, sort: 'name' } },
    })
    const typeCode = new Map((types ?? []).map((ty) => [ty.uid, ty.code as string]))
    const evidenceKids = ((kids as { data?: Array<{ uid: string; name?: string; asset_type_uid?: string }> } | undefined)?.data ?? [])
        .filter((k) => {
            const c = k.asset_type_uid ? typeCode.get(k.asset_type_uid) ?? '' : ''
            return c === 'room' || c.startsWith('room_') || c.startsWith('hvac_')
        })
    const rows = codes.map((code) => {
        const def = (defs ?? []).find((d) => d.code === code)
        const v = def ? (values ?? []).find((x) => x.property_uid === def.uid) : undefined
        return { code, name: def ? getLocalizedName(def.name as Record<string, string>, locale) : code, value: v?.value, source: v?.source_code }
    })
    return (
        <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground" data-testid={`occupation-criterion-evidence-${ruleCode}`}>
            {rows.map((r) => (
                <li key={r.code} data-testid={`occupation-criterion-evidence-${ruleCode}-${r.code}`}>
                    <span>{r.name}: </span>
                    <span className="text-foreground">{r.value ?? t('occupation.criteria.no_value')}</span>
                    {r.source && <span> ({t('occupation.criteria.source', { source: r.source })})</span>}
                </li>
            ))}
            {evidenceKids.map((k) => (
                <li key={k.uid} data-testid={`occupation-criterion-evidence-${ruleCode}-asset-${k.uid}`}>
                    <span>{t('occupation.criteria.evidence_asset')}: </span>
                    <span className="text-foreground">{k.name}</span>
                </li>
            ))}
        </ul>
    )
}

function CriteriaCard({ org, assetUid, gate }: { org: string; assetUid: string; gate: Lettability }) {
    const { t } = useTranslation('leases')
    const { locale } = useLocale()
    const [open, setOpen] = useState<string | null>(null)
    if (gate.criteria.length === 0) return null
    return (
        <Card data-testid="occupation-criteria">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('occupation.criteria.title')}</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
                {gate.criteria.map((c) => (
                    <div key={c.rule_code} className="rounded-md border border-border p-2" data-testid={`occupation-criterion-${c.rule_code}`} data-status={c.status}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium">{label(c.label, locale) || c.rule_code}</span>
                            <div className="flex items-center gap-2">
                                {c.deadline && <span className="text-xs text-muted-foreground">{t('occupation.criteria.deadline', { date: c.deadline })}</span>}
                                <Badge variant={CRITERION_VARIANT[c.status] ?? 'warning'} data-testid={`occupation-criterion-status-${c.rule_code}`}>
                                    {t(`occupation.criteria.status.${c.status}`, { defaultValue: c.status })}
                                </Badge>
                                {c.property_codes.length > 0 && (
                                    <Button variant="outline" size="sm" onClick={() => setOpen(open === c.rule_code ? null : c.rule_code)}
                                        data-testid={`occupation-criterion-edit-${c.rule_code}`}>
                                        {open === c.rule_code ? t('occupation.criteria.close') : t('occupation.criteria.edit')}
                                    </Button>
                                )}
                            </div>
                        </div>
                        {c.property_codes.length > 0 && (
                            <CriterionEvidence org={org} assetUid={assetUid} ruleCode={c.rule_code} codes={c.property_codes} />
                        )}
                        {open === c.rule_code && (
                            <div className="mt-2" data-testid={`occupation-criterion-properties-${c.rule_code}`}>
                                <AssetProperties org={org} assetUid={assetUid} variant="table" groupByCategory
                                    baseCodes={c.property_codes} restrictToBaseCodes />
                            </div>
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    )
}

function ConfirmationsCard({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate } = useFormatters()
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/lettability/confirmations', {
        params: { path: { orgCode: org, assetUid } },
    })
    const rows = (data?.data ?? []) as LettabilityConfirmation[]
    if (rows.length === 0) return null
    return (
        <Card data-testid="occupation-confirmations">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('occupation.confirmations.title')}</CardTitle></CardHeader>
            <CardContent>
                <ul className="space-y-2 text-sm" data-testid="occupation-confirmations-list">
                    {rows.map((c) => (
                        <li key={c.uid} className="rounded-md border border-border p-2" data-testid={`occupation-confirmation-${c.uid}`}>
                            <div className="flex flex-wrap items-center gap-2">
                                <LettabilityStatusBadge status={c.status} />
                                <span>{t(`occupation.confirmations.action.${c.action}`, { defaultValue: c.action })}</span>
                                <span className="text-xs text-muted-foreground">{formatDate(c.created_at)} · {c.confirmed_by}</span>
                            </div>
                            <p className="mt-1">{c.reason}</p>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    )
}

function SubtreeOccupation({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const canReadContracts = useCan('contracts.read')
    const { formatMoney } = useFormatters()
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/assets/{assetUid}/lettability/lots', {
        params: { path: { orgCode: org, assetUid } },
    })
    const { data: rents } = useRentalCompliance(org, assetUid, canReadContracts)
    const lots = data as LettabilityLots | undefined
    return (
        <div className="space-y-4" data-testid="occupation-tab">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {(['allowed', 'blocked', 'not_evaluated'] as Status[]).map((s) => (
                    <Card key={s}>
                        <CardContent className="flex items-center justify-between p-4">
                            <LettabilityStatusBadge status={s} />
                            <span className="text-2xl font-semibold" data-testid={`occupation-count-${s}`}>{lots?.counts[s] ?? '—'}</span>
                        </CardContent>
                    </Card>
                ))}
            </div>
            {canReadContracts && rents && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">{t('occupation.rents.at_risk')}</div>
                        <div className="text-lg font-semibold" data-testid="occupation-rent-at-risk">{formatMoney(rents.rent_at_risk, rents.currency)}</div>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="text-xs text-muted-foreground">{t('occupation.rents.unverified')}</div>
                        <div className="text-lg font-semibold" data-testid="occupation-rent-unverified">{formatMoney(rents.rent_unverified, rents.currency)}</div>
                    </CardContent></Card>
                </div>
            )}
            {isLoading || !lots ? <Skeleton className="h-40 w-full" /> : lots.lots.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="occupation-lots-empty">{t('occupation.lots.empty')}</p>
            ) : (
                <Table data-testid="occupation-lots">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('occupation.lots.lot')}</TableHead>
                            <TableHead>{t('occupation.lots.status')}</TableHead>
                            <TableHead>{t('occupation.lots.reasons')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {lots.lots.map((l) => (
                            <TableRow key={l.asset_uid} data-testid={`occupation-lot-row-${l.asset_uid}`}>
                                <TableCell>
                                    <Link to="/$org/assets/$assetUid" params={{ org, assetUid: l.asset_uid }} search={{ tab: 'occupation' }}
                                        className="text-primary hover:underline" data-testid={`occupation-lot-link-${l.asset_uid}`}>
                                        {l.name || l.asset_uid}
                                    </Link>
                                </TableCell>
                                <TableCell><LettabilityStatusBadge status={l.status} testId={`occupation-lot-status-${l.asset_uid}`} /></TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {[
                                        ...l.reasons.map((r) => t(`occupation.reason.${r.code}`)),
                                        ...(l.blocker_count > 0 ? [t('occupation.lots.blockers', { count: l.blocker_count })] : []),
                                    ].join(' · ') || '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    )
}
